import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiFilter,
  FiFileText,
  FiAlertTriangle,
  FiSearch,
  FiCalendar,
  FiDollarSign,
  FiLayers,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiLoader,
  FiEdit,
  FiX,
  FiUser,
  FiInfo,
  FiTrash2,
  FiAlertCircle,
} from 'react-icons/fi';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI, planosAPI, clientesAPI } from '../services/api';
import VeiculosPaginacao from '../components/veiculos/VeiculosPaginacao';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.xl};
  border: ${({ $variant }) =>
    $variant === 'secondary' ? `1px solid ${theme.colors.primary[500]}` : 'none'};
  background: ${({ $variant }) =>
    $variant === 'primary' ? theme.colors.primary[500] : 'transparent'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? theme.colors.text.primary : theme.colors.primary[500]};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary' ? theme.colors.primary[600] : theme.colors.primary[500]}20;
    box-shadow: ${({ $variant }) =>
      $variant === 'primary' ? `0 4px 12px ${theme.colors.primary[500]}40` : 'none'};
  }
`;

const FiltersCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[2]};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const FiltersFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: ${({ $primary }) => ($primary ? theme.colors.primary[500] : 'transparent')};
  color: ${({ $primary }) =>
    $primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${({ $primary }) =>
      $primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${({ $primary }) =>
      $primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${({ $primary }) =>
      $primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: ${theme.spacing[5]};
  }
`;

const ContratoCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }

  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
`;

const CardTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  margin: 0;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const CardInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const CardActions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const CardActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid
    ${({ $primary, $details, $edit, $danger }) =>
      $primary
        ? theme.colors.primary[500]
        : $details
          ? theme.colors.primary[400]
          : $edit
            ? theme.colors.warning[500]
            : $danger
              ? theme.colors.error[500]
              : theme.colors.border.primary};
  background: ${({ $primary, $details, $edit, $danger }) =>
    $primary
      ? theme.colors.primary[500]
      : $details
        ? theme.colors.primary[400]
        : $edit
          ? theme.colors.warning[500]
          : $danger
            ? theme.colors.error[500]
            : 'transparent'};
  color: ${({ $primary, $details, $edit, $danger }) =>
    $primary || $details || $edit || $danger
      ? theme.colors.text.primary
      : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  min-height: 44px;

  &:hover {
    background: ${({ $primary, $details, $edit, $danger }) =>
      $primary
        ? theme.colors.primary[600]
        : $details
          ? theme.colors.primary[500]
          : $edit
            ? theme.colors.warning[600]
            : $danger
              ? theme.colors.error[600]
              : theme.colors.background.primary};
    color: ${({ $primary, $details, $edit, $danger }) =>
      $primary || $details || $edit || $danger
        ? theme.colors.text.primary
        : theme.colors.primary[500]};
    border-color: ${({ $primary, $details, $edit, $danger }) =>
      $primary
        ? theme.colors.primary[600]
        : $details
          ? theme.colors.primary[500]
          : $edit
            ? theme.colors.warning[600]
            : $danger
              ? theme.colors.error[600]
              : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    font-size: ${theme.fontSizes.xs};
    min-height: 40px;
  }
`;

const InfoIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: ${theme.spacing[1]};
  cursor: pointer;
  color: ${theme.colors.text.muted};

  &:hover {
    color: ${theme.colors.primary[500]};
  }

  svg {
    font-size: ${theme.fontSizes.sm};
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  z-index: 1000;
  min-width: 250px;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${theme.colors.background.primary};
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 7px solid transparent;
    border-top-color: ${theme.colors.border.primary};
    margin-top: -1px;
  }
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
`;

const TooltipLabel = styled.span`
  color: ${theme.colors.text.secondary};
`;

const TooltipValue = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: 600;
`;


const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  text-transform: capitalize;
  color: ${({ $status }) => {
    switch ($status) {
      case 'ATIVO':
        return theme.colors.success[600];
      case 'CANCELADO':
        return theme.colors.error[600];
      case 'EXPIRADO':
        return theme.colors.warning[700];
      default:
        return theme.colors.text.secondary;
    }
  }};
  background: ${({ $status }) => {
    switch ($status) {
      case 'ATIVO':
        return theme.colors.success[100];
      case 'CANCELADO':
        return theme.colors.error[100];
      case 'EXPIRADO':
        return theme.colors.warning[100];
      default:
        return theme.colors.background.primary;
    }
  }};
`;


const GhostButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
`;

const EmptyTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  max-width: 420px;
`;




const SummaryChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.background.primary};
  font-weight: 600;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 600px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalInfoBox = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin: ${theme.spacing[4]} 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ModalInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const ModalInfoLabel = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const ModalInfoValue = styled.span`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
  margin-top: ${theme.spacing[6]};
`;

const SpinningIcon = styled.span`
  display: inline-flex;
  align-items: center;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${(props) => (props.$danger ? theme.colors.error[500] : theme.colors.border.primary)};
  background: ${(props) => (props.$danger ? theme.colors.error[500] : 'transparent')};
  color: ${(props) => (props.$danger ? theme.colors.text.primary : theme.colors.text.secondary)};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${(props) => (props.$danger ? theme.colors.error[600] : theme.colors.background.primary)};
    color: ${(props) => {
      return props.$danger ? theme.colors.text.primary : theme.colors.primary[500];
    }};
    border-color: ${(props) => (props.$danger ? theme.colors.error[600] : theme.colors.primary[500])};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const STATUS_OPTIONS = [
  { label: 'Ativo', value: 'ATIVO' },
  { label: 'Cancelado', value: 'CANCELADO' },
  { label: 'Expirado', value: 'EXPIRADO' },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'UTC',
      }).format(new Date(value))
    : '—';

const Contratos = () => {
  const navigate = useNavigate();
  const [contratos, setContratos] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false,
  });
  // Função para obter o primeiro dia do mês atual no formato YYYY-MM-DD
  const getPrimeiroDiaMes = () => {
    const agora = new Date();
    const ano = agora.getFullYear();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    return `${ano}-${mes}-01`;
  };

  // Função para obter o último dia do mês atual no formato YYYY-MM-DD
  const getUltimoDiaMes = () => {
    const agora = new Date();
    const ano = agora.getFullYear();
    const mes = agora.getMonth() + 1;
    const ultimoDia = new Date(ano, mes, 0).getDate();
    return `${ano}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
  };

  const [filtros, setFiltros] = useState({
    cliente_id: '',
    status: 'ATIVO',
    plano_id: '',
    data_inicio: '',
    data_fim: '',
    page: 1,
    limit: 20,
  });
  const [planos, setPlanos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState({});
  const [modalRescisao, setModalRescisao] = useState({
    aberto: false,
    contrato: null,
    dadosMulta: null,
    carregando: false,
    processando: false,
  });

  const carregarPlanos = async () => {
    try {
      const response = await planosAPI.listar({ limit: 100, page: 1, ativo: 'true' });
      if (response.data?.success) {
        const planosOrdenados = (response.data.data || []).sort((a, b) => {
          const valorA = Number.parseFloat(a.valor_mensal || 0);
          const valorB = Number.parseFloat(b.valor_mensal || 0);
          return valorA - valorB;
        });
        setPlanos(planosOrdenados);
      }
    } catch (error) {
      console.error('Erro ao carregar planos para filtros', error);
    }
  };

  const carregarClientes = async () => {
    try {
      const response = await clientesAPI.listar({ limit: 1000, page: 1 });
      if (response.data?.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes para filtros', error);
    }
  };

  const carregarContratos = async (params = filtros) => {
    try {
      setLoading(true);
      setErro(null);
      const paramsAdaptados = { ...params };
      // Transforma filtros de data para buscar contratos ATIVOS dentro do período informado
      // Quando o usuário informa data_inicio e data_fim, busca contratos que estão ativos nesse período
      // Um contrato está ativo no período se:
      // - data_inicio_contrato <= data_fim_filtro (começa antes ou durante)
      // - data_fim_contrato >= data_inicio_filtro OU data_fim_contrato IS NULL (termina depois ou durante, ou não tem data fim)
      if (paramsAdaptados.data_inicio && paramsAdaptados.data_inicio.trim() !== '') {
        paramsAdaptados.data_inicio_de = paramsAdaptados.data_inicio;
        delete paramsAdaptados.data_inicio;
      }
      if (paramsAdaptados.data_fim && paramsAdaptados.data_fim.trim() !== '') {
        paramsAdaptados.data_fim_ate = paramsAdaptados.data_fim;
        delete paramsAdaptados.data_fim;
      }
      // Remove campos vazios para não enviar filtros desnecessários
      Object.keys(paramsAdaptados).forEach(key => {
        if (paramsAdaptados[key] === '' || paramsAdaptados[key] === null || paramsAdaptados[key] === undefined) {
          delete paramsAdaptados[key];
        }
      });
      const response = await contratosAPI.listar(paramsAdaptados);
      if (response.data?.success) {
        const contratosRecebidos = response.data.data || [];
        setContratos(contratosRecebidos);
        setPagination(response.data.pagination || pagination);
      } else {
        console.error('Resposta da API sem sucesso:', response.data);
        setErro('Não foi possível carregar os contratos.');
      }
    } catch (error) {
      console.error('Erro ao carregar contratos', error);
      if (error.response?.data?.error) {
        setErro(error.response.data.error);
      } else {
        setErro('Erro ao carregar contratos. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPlanos();
    carregarClientes();
    carregarContratos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const aplicarFiltros = () => {
    const params = { ...filtros, page: 1 };
    setFiltros(params);
    carregarContratos(params);
  };

  const limparFiltros = () => {
    const params = {
      cliente_id: '',
      status: 'ATIVO',
      plano_id: '',
      data_inicio: '',
      data_fim: '',
      page: 1,
      limit: filtros.limit,
    };
    setFiltros(params);
    carregarContratos(params);
  };

  const temFiltrosAtivos = useMemo(() => {
    return Object.entries(filtros).some(
      ([key, value]) => key !== 'page' && key !== 'limit' && value !== '' && value !== null,
    );
  }, [filtros]);

  const handlePageChange = (nextPage) => {
    const params = { ...filtros, page: nextPage };
    setFiltros(params);
    carregarContratos(params);
  };


  const contratosVigentes = useMemo(() => {
    return contratos;
  }, [contratos]);

  const obterNomeCliente = useMemo(() => {
    return (clienteId) => {
      const cliente = clientes.find((c) => c.id === clienteId);
      return cliente ? cliente.nome_fantasia || cliente.nome_social : `ID ${clienteId}`;
    };
  }, [clientes]);

  const calcularValorTotal = useMemo(() => {
    return (contrato) => {
      const valorBase = Number.parseFloat(contrato.valor_mensal || 0);
      if (contrato.ciclo_atual && contrato.ciclo_atual.valor_extensoes) {
        return valorBase + contrato.ciclo_atual.valor_extensoes;
      }
      return valorBase;
    };
  }, []);

  const calcularCreditosTotais = useMemo(() => {
    return (contrato) => {
      const creditosBase = Number.parseInt(contrato.creditos_mensais || 0, 10);
      if (contrato.ciclo_atual && contrato.ciclo_atual.creditos_extensoes) {
        return creditosBase + contrato.ciclo_atual.creditos_extensoes;
      }
      return creditosBase;
    };
  }, []);

  const temExtensoes = useMemo(() => {
    return (contrato) => {
      return (
        contrato.ciclo_atual &&
        ((contrato.ciclo_atual.creditos_extensoes > 0) ||
          (contrato.ciclo_atual.valor_extensoes > 0) ||
          (contrato.ciclo_atual.extensoes?.length > 0))
      );
    };
  }, []);

  const resumo = useMemo(() => {
    const total = pagination.totalItems || 0;
    const ativos = contratos.filter((contrato) => contrato.status === 'ATIVO').length;
    const cancelados = contratos.filter((contrato) => contrato.status === 'CANCELADO').length;
    const expirados = contratos.filter((contrato) => contrato.status === 'EXPIRADO').length;
    return { total, ativos, cancelados, expirados };
  }, [contratos, pagination.totalItems]);

  const abrirModalRescisao = async (contrato) => {
    try {
      setModalRescisao({
        aberto: true,
        contrato,
        dadosMulta: null,
        carregando: true,
        processando: false,
      });

      const response = await contratosAPI.calcularMultaRescisao(contrato.id);
      if (response.data?.success) {
        setModalRescisao((prev) => ({
          ...prev,
          dadosMulta: response.data.data,
          carregando: false,
        }));
      } else {
        setErro(response.data?.error || 'Erro ao calcular multa de recisão');
        setModalRescisao((prev) => ({
          ...prev,
          aberto: false,
          carregando: false,
        }));
      }
    } catch (error) {
      console.error('Erro ao calcular multa de recisão', error);
      setErro(error.response?.data?.error || 'Erro ao calcular multa de recisão');
      setModalRescisao((prev) => ({
        ...prev,
        aberto: false,
        carregando: false,
      }));
    }
  };

  const fecharModalRescisao = () => {
    setModalRescisao({
      aberto: false,
      contrato: null,
      dadosMulta: null,
      carregando: false,
      processando: false,
    });
  };

  const confirmarRescisao = async () => {
    if (!modalRescisao.contrato || !modalRescisao.dadosMulta) return;

    try {
      setModalRescisao((prev) => ({ ...prev, processando: true }));

      const response = await contratosAPI.recindirContrato(modalRescisao.contrato.id);
      if (response.data?.success) {
        // Recarregar contratos e fechar modal
        await carregarContratos();
        fecharModalRescisao();
        // Mostrar mensagem de sucesso (opcional - você pode adicionar um toast aqui)
      } else {
        setErro(response.data?.error || 'Erro ao recindir contrato');
        setModalRescisao((prev) => ({ ...prev, processando: false }));
      }
    } catch (error) {
      console.error('Erro ao recindir contrato', error);
      setErro(error.response?.data?.error || 'Erro ao recindir contrato');
      setModalRescisao((prev) => ({ ...prev, processando: false }));
    }
  };

  const listaVazia = !loading && contratos.length === 0 && !erro;

  return (
    <Layout>
      <Container>
        <Header>
          <HeaderGroup>
            <Title>Gestão de Contratos</Title>
            <Subtitle>
              <SummaryChip>
                <FiFileText />
                {resumo.total} contratos
              </SummaryChip>
              <SummaryChip>
                <FiCheckCircle />
                {resumo.ativos} ativos
              </SummaryChip>
              <SummaryChip>
                <FiXCircle />
                {resumo.cancelados} cancelados
              </SummaryChip>
              <SummaryChip>
                <FiClock />
                {resumo.expirados} expirados
              </SummaryChip>
            </Subtitle>
          </HeaderGroup>
          <Actions>
            <Button $variant="primary" onClick={() => navigate('/gestao/contratos/novo')}>
              <FiPlus />
              Novo contrato
            </Button>
          </Actions>
        </Header>

        <FiltersCard>
          <FiltersHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
              <FiFilter />
              Filtros de busca
            </div>
            <FiltersFooter>
              <ActionButton onClick={limparFiltros} disabled={!temFiltrosAtivos}>
                <FiX />
                Limpar Filtros
              </ActionButton>
              <ActionButton $primary onClick={aplicarFiltros} disabled={loading}>
                <FiSearch />
                {loading ? 'Buscando...' : 'Buscar'}
              </ActionButton>
            </FiltersFooter>
          </FiltersHeader>

          <FiltersGrid>
            <FilterGroup>
              <Label>Cliente</Label>
              <Select
                value={filtros.cliente_id}
                onChange={(e) => handleFiltroChange('cliente_id', e.target.value)}
              >
                <option value="">Todos os clientes</option>
                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome_fantasia || cliente.nome_social}
                  </option>
                ))}
              </Select>
            </FilterGroup>
            <FilterGroup>
              <Label>Status</Label>
              <Select
                value={filtros.status}
                onChange={(e) => handleFiltroChange('status', e.target.value)}
              >
                <option value="">Todos</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FilterGroup>
            <FilterGroup>
              <Label>Plano</Label>
              <Select
                value={filtros.plano_id}
                onChange={(e) => handleFiltroChange('plano_id', e.target.value)}
              >
                <option value="">Todos</option>
                {planos.map((plano) => (
                  <option key={plano.id} value={plano.id}>
                    {plano.nome}
                  </option>
                ))}
              </Select>
            </FilterGroup>
            <FilterGroup>
              <Label>Data início</Label>
              <Input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => handleFiltroChange('data_inicio', e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <Label>Data fim</Label>
              <Input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => handleFiltroChange('data_fim', e.target.value)}
              />
            </FilterGroup>
          </FiltersGrid>
        </FiltersCard>

        <ContentCard>
          {erro && (
            <EmptyState>
              <EmptyIcon>
                <FiAlertTriangle />
              </EmptyIcon>
              <EmptyTitle>Não foi possível carregar os contratos</EmptyTitle>
              <EmptyText>{erro}</EmptyText>
              <Button $variant="primary" onClick={() => carregarContratos()}>
                Tentar novamente
              </Button>
            </EmptyState>
          )}

          {loading && !erro && (
            <EmptyState>
              <EmptyIcon>
                <FiLoader />
              </EmptyIcon>
              <EmptyTitle>Carregando contratos</EmptyTitle>
              <EmptyText>Aguarde enquanto buscamos as informações mais recentes.</EmptyText>
            </EmptyState>
          )}

          {listaVazia && (
            <EmptyState>
              <EmptyIcon>
                <FiSearch />
              </EmptyIcon>
              <EmptyTitle>Nenhum contrato encontrado</EmptyTitle>
              <EmptyText>
                Ajuste os filtros ou cadastre um novo contrato para começar a operação.
              </EmptyText>
              <Button $variant="primary" onClick={() => navigate('/gestao/contratos/novo')}>
                <FiPlus />
                Cadastrar contrato
              </Button>
            </EmptyState>
          )}

          {!loading && !erro && contratosVigentes.length > 0 && (
            <>
              <CardsGrid>
                {contratosVigentes.map((contrato) => (
                  <ContratoCard key={contrato.id}>
                    <CardHeader>
                      <CardTitle>Contrato #{contrato.id}</CardTitle>
                      <StatusBadge $status={contrato.status}>
                        {contrato.status?.toLowerCase()}
                      </StatusBadge>
                    </CardHeader>
                    <CardInfo>
                      <CardInfoRow>
                        <FiUser />
                        <span>
                          <strong>Cliente:</strong> {obterNomeCliente(contrato.cliente_id)}
                        </span>
                      </CardInfoRow>
                      <CardInfoRow>
                        <FiCalendar />
                        <span>
                          <strong>Período:</strong> {formatDate(contrato.data_inicio)} -{' '}
                          {contrato.data_fim ? formatDate(contrato.data_fim) : 'Indeterminado'}
                        </span>
                      </CardInfoRow>
                      <CardInfoRow>
                        <FiDollarSign />
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Valor:</strong> {formatCurrency(calcularValorTotal(contrato))}/mês
                          <InfoIcon
                            onMouseEnter={() =>
                              setTooltipVisible((prev) => ({ ...prev, [`valor-${contrato.id}`]: true }))
                            }
                            onMouseLeave={() =>
                              setTooltipVisible((prev) => ({ ...prev, [`valor-${contrato.id}`]: false }))
                            }
                          >
                            <FiInfo />
                            <Tooltip $visible={tooltipVisible[`valor-${contrato.id}`]}>
                              <TooltipContent>
                                <TooltipRow>
                                  <TooltipLabel>Plano original:</TooltipLabel>
                                  <TooltipValue>
                                    {formatCurrency(contrato.valor_mensal || 0)}
                                  </TooltipValue>
                                </TooltipRow>
                                <TooltipRow>
                                  <TooltipLabel>Extensões:</TooltipLabel>
                                  <TooltipValue>
                                    {formatCurrency(contrato.ciclo_atual?.valor_extensoes || 0)}
                                  </TooltipValue>
                                </TooltipRow>
                                <TooltipRow>
                                  <TooltipLabel>Total:</TooltipLabel>
                                  <TooltipValue>
                                    {formatCurrency(calcularValorTotal(contrato))}
                                  </TooltipValue>
                                </TooltipRow>
                              </TooltipContent>
                            </Tooltip>
                          </InfoIcon>
                        </span>
                      </CardInfoRow>
                      <CardInfoRow>
                        <FiLayers />
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Créditos:</strong> {calcularCreditosTotais(contrato)} créditos/mês
                          <InfoIcon
                            onMouseEnter={() =>
                              setTooltipVisible((prev) => ({ ...prev, [`creditos-${contrato.id}`]: true }))
                            }
                            onMouseLeave={() =>
                              setTooltipVisible((prev) => ({ ...prev, [`creditos-${contrato.id}`]: false }))
                            }
                          >
                            <FiInfo />
                            <Tooltip $visible={tooltipVisible[`creditos-${contrato.id}`]}>
                              <TooltipContent>
                                <TooltipRow>
                                  <TooltipLabel>Plano original:</TooltipLabel>
                                  <TooltipValue>
                                    {contrato.creditos_mensais || 0} créditos
                                  </TooltipValue>
                                </TooltipRow>
                                <TooltipRow>
                                  <TooltipLabel>Extensões:</TooltipLabel>
                                  <TooltipValue>
                                    {contrato.ciclo_atual?.creditos_extensoes || 0} créditos
                                  </TooltipValue>
                                </TooltipRow>
                                <TooltipRow>
                                  <TooltipLabel>Total:</TooltipLabel>
                                  <TooltipValue>
                                    {calcularCreditosTotais(contrato)} créditos
                                  </TooltipValue>
                                </TooltipRow>
                              </TooltipContent>
                            </Tooltip>
                          </InfoIcon>
                        </span>
                      </CardInfoRow>
                      {contrato.ciclo_atual && (
                        <>
                          <CardInfoRow>
                            <FiLayers />
                            <span>
                              <strong>Saldo atual:</strong> {contrato.ciclo_atual.creditos_saldo} /{' '}
                              {contrato.ciclo_atual.creditos_totais}
                            </span>
                          </CardInfoRow>
                          <CardInfoRow>
                            <FiCalendar />
                            <span>
                              <strong>Vigência:</strong> {formatDate(contrato.ciclo_atual.inicio)} -{' '}
                              {formatDate(contrato.ciclo_atual.fim)}
                            </span>
                          </CardInfoRow>
                        </>
                      )}
                    </CardInfo>
                    <CardActions>
                      <CardActionButton $details onClick={() => navigate(`/gestao/contratos/${contrato.id}`)}>
                        <FiFileText />
                        Detalhes
                      </CardActionButton>
                      {contrato.status === 'ATIVO' && (
                        <>
                          <CardActionButton $edit onClick={() => navigate(`/gestao/contratos/${contrato.id}/editar`)}>
                            <FiEdit />
                            Editar
                          </CardActionButton>
                          <CardActionButton $danger onClick={() => abrirModalRescisao(contrato)}>
                            <FiTrash2 />
                            Recindir
                          </CardActionButton>
                        </>
                      )}
                    </CardActions>
                  </ContratoCard>
                ))}
              </CardsGrid>

              <VeiculosPaginacao
                pagination={{
                  currentPage: pagination.currentPage,
                  totalPages: pagination.totalPages || 1,
                  totalItems: pagination.totalItems,
                  itemsPerPage: pagination.itemsPerPage || 20,
                  hasNextPage: pagination.hasNextPage,
                  hasPrevPage: pagination.hasPrevPage,
                }}
                onPageChange={(page) => handlePageChange(page)}
                onItemsPerPageChange={(itemsPerPage) => {
                  const params = { ...filtros, page: 1, limit: itemsPerPage };
                  setFiltros(params);
                  carregarContratos(params);
                }}
                loading={loading}
                itemLabel="contratos"
              />
            </>
          )}
        </ContentCard>
      </Container>

      {/* Modal de Recisão */}
      {modalRescisao.aberto && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !modalRescisao.processando && fecharModalRescisao()}>
          <ModalContent>
            <ModalTitle>
              <FiAlertCircle />
              Recindir Contrato
            </ModalTitle>
            {modalRescisao.carregando ? (
              <ModalMessage>Calculando multa de recisão...</ModalMessage>
            ) : modalRescisao.dadosMulta ? (
              <>
                <ModalMessage>
                  Tem certeza que deseja recindir este contrato? Uma cobrança de multa será gerada automaticamente no cartão padrão do cliente.
                </ModalMessage>
                <ModalInfoBox>
                  <ModalInfoRow>
                    <ModalInfoLabel>Contrato:</ModalInfoLabel>
                    <ModalInfoValue>#{modalRescisao.contrato.id}</ModalInfoValue>
                  </ModalInfoRow>
                  <ModalInfoRow>
                    <ModalInfoLabel>Período:</ModalInfoLabel>
                    <ModalInfoValue>
                      {formatDate(modalRescisao.dadosMulta.dataInicio)} -{' '}
                      {modalRescisao.dadosMulta.dataFim ? formatDate(modalRescisao.dadosMulta.dataFim) : 'Indeterminado'}
                    </ModalInfoValue>
                  </ModalInfoRow>
                  <ModalInfoRow>
                    <ModalInfoLabel>Mensalidades restantes:</ModalInfoLabel>
                    <ModalInfoValue>{modalRescisao.dadosMulta.mensalidadesRestantes} meses</ModalInfoValue>
                  </ModalInfoRow>
                  <ModalInfoRow>
                    <ModalInfoLabel>Valor das mensalidades:</ModalInfoLabel>
                    <ModalInfoValue>{formatCurrency(modalRescisao.dadosMulta.valorMensalidadesRestantes)}</ModalInfoValue>
                  </ModalInfoRow>
                  <ModalInfoRow>
                    <ModalInfoLabel>Multa (20%):</ModalInfoLabel>
                    <ModalInfoValue style={{ color: theme.colors.error[600] }}>
                      {formatCurrency(modalRescisao.dadosMulta.multa)}
                    </ModalInfoValue>
                  </ModalInfoRow>
                  <ModalInfoRow style={{ borderTop: `1px solid ${theme.colors.border.primary}`, paddingTop: theme.spacing[3] }}>
                    <ModalInfoLabel style={{ fontWeight: 700 }}>Total a cobrar:</ModalInfoLabel>
                    <ModalInfoValue style={{ fontSize: theme.fontSizes.lg, color: theme.colors.error[600], fontWeight: 700 }}>
                      {formatCurrency(modalRescisao.dadosMulta.valorTotal)}
                    </ModalInfoValue>
                  </ModalInfoRow>
                </ModalInfoBox>
                <ModalActions>
                  <ModalButton onClick={fecharModalRescisao} disabled={modalRescisao.processando}>
                    <FiX />
                    Cancelar
                  </ModalButton>
                  <ModalButton $danger onClick={confirmarRescisao} disabled={modalRescisao.processando}>
                    {modalRescisao.processando ? (
                      <>
                        <SpinningIcon>
                          <FiLoader />
                        </SpinningIcon>
                        Processando...
                      </>
                    ) : (
                      <>
                        <FiTrash2 />
                        Confirmar Recisão
                      </>
                    )}
                  </ModalButton>
                </ModalActions>
              </>
            ) : (
              <ModalMessage>Erro ao calcular multa de recisão.</ModalMessage>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default Contratos;


